import { useMutation } from '@apollo/client';

import bg from '@assets/images/static/background-quiz.jpg';
import { useLingui } from '@lingui/react';
import { ActivityType } from '@zeus/index';

import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { GQL_CREATE_TRACKING } from '~/gql/tracking/tracking';
import { GQL_UPDATE_TRACKING_THEMATIQUE } from '~/gql/tracking-thematique/tracking-thematique';
import useInactivityDetect from '~/helpers/hooks/activity-detect';
import useSoundAudio from '~/helpers/hooks/soundAudio';
import { useTimer } from '~/helpers/hooks/timer';
import useConnectionStore from '~/helpers/store/connection/connection';
import {
  changeActivityOrder,
  getStatus,
  parseTextToJSX,
} from '~/pages/components/game/activities-forms/utils';
import { SDivParsedItem } from '~/pages/components/styles/styles';
import Button from '~/pages/components/ui/button';
import { FeedbackItem } from '~/pages/components/ui/feedback-item';
import { QcuQcmItem } from '~/pages/components/ui/qcu-qcm-item';
import { QuestionItem } from '~/pages/components/ui/question-item';
import { TActivity, TAnswer } from '~/utils/airtable/types/types';
import { renderFullPath } from '~/utils/common/utils';

type IProps = {
  currentActivity: TActivity;
  nextActivity: () => void;
};

function Qcu({ currentActivity, nextActivity }: IProps) {
  const { i18n } = useLingui();
  const [selectedAnswer, setSelectedAnswer] = useState<TAnswer | undefined>(
    undefined
  );
  const { currentTrackingThematiqueId, setCurrentTrackings, currentTrackings } =
    useConnectionStore();
  const [submitted, setSubmitted] = useState<boolean>(false);

  const { time, start, stop, clear } = useTimer();
  const { playSound } = useSoundAudio();

  const [updateTrackingThematique] = useMutation(
    GQL_UPDATE_TRACKING_THEMATIQUE
  );

  const [createTracking] = useMutation(GQL_CREATE_TRACKING);

  const isActive = useInactivityDetect({ timeout: 5000 });

  useEffect(() => {
    if (submitted) {
      if (selectedAnswer?.isCorrect) playSound('feedbackGood');
      else playSound('feedbackWrong');
    }
  }, [submitted]);

  useEffect(() => {
    if (!isActive) {
      stop();
    } else {
      start();
    }
  }, [isActive]);

  useEffect(() => {
    setSelectedAnswer(undefined);
    setSubmitted(false);
    start();
    if (currentTrackingThematiqueId) {
      //setCurrentActivityOrder(currentActivity.order);
      setCurrentTrackings(
        changeActivityOrder(
          currentTrackingThematiqueId,
          currentActivity.order,
          currentTrackings
        )
      );
    }
  }, [currentActivity]);

  function handleClickValid() {
    setSubmitted(true);
    stop();
  }

  async function handleNext() {
    try {
      if (currentTrackingThematiqueId && selectedAnswer) {
        await updateTrackingThematique({
          variables: { id: currentTrackingThematiqueId, time: time },
        });

        await createTracking({
          variables: {
            trackingThematiqueId: currentTrackingThematiqueId,
            activityOrder: currentActivity.order,
            activityType: currentActivity.type as ActivityType,
            answers: [selectedAnswer.text],
            isCorrect: selectedAnswer.isCorrect,
            time: time === 0 ? 1 : time,
          },
        });
      }
    } finally {
      clear();

      playSound('click');
      nextActivity();
    }
  }

  function handleClick(answer: TAnswer) {
    playSound('select');
    !selectedAnswer ? setSelectedAnswer(answer) : setSelectedAnswer(undefined);
  }

  return (
    <Container $background={bg}>
      <SDivBlur $withBackground={submitted}>
        <SDivContent>
          <SDivQuestion>
            {submitted ? (
              <>
                <FeedbackItem
                  $margin="0 0 5px 0"
                  characterImg={renderFullPath(currentActivity.characterImg)}
                >
                  {selectedAnswer?.isCorrect ? (
                    <SDivParsedItem>
                      {parseTextToJSX(currentActivity.feedbackCorrect ?? '')}
                    </SDivParsedItem>
                  ) : (
                    <SDivParsedItem>
                      {parseTextToJSX(currentActivity.feedbackIncorrect ?? '')}
                    </SDivParsedItem>
                  )}
                </FeedbackItem>
              </>
            ) : (
              <>
                <QuestionItem
                  text={currentActivity.text}
                  characterImg={renderFullPath(currentActivity.characterImg)}
                  $margin="0 0 5px 0"
                  advice={i18n._('activities.advice.qcu')}
                />
              </>
            )}
          </SDivQuestion>

          <SDivQcuQcm>
            {currentActivity.answers?.map((answer, index) => (
              <QcuQcmItem
                key={index}
                submitted={submitted}
                onClick={() => handleClick(answer)}
                selected={answer.text === selectedAnswer?.text}
                status={getStatus(answer, selectedAnswer)}
              >
                {answer.text}
              </QcuQcmItem>
            ))}
          </SDivQcuQcm>
        </SDivContent>
        <SDivBtn>
          <Button
            color={submitted ? 'secondary' : 'primary'}
            disabled={!submitted && selectedAnswer === undefined}
            onClick={submitted ? handleNext : handleClickValid}
          >
            {submitted ? i18n._('actions.next') : i18n._('actions.valid')}
          </Button>
        </SDivBtn>
      </SDivBlur>
    </Container>
  );
}

export default Qcu;

const Container = styled.div<{ $background?: string }>`
  align-items: center;
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-color: #c0dce3;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
`;

const SDivQuestion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 0 0 30%;
  width: 100%;
`;

const SDivContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0 0 85%;
  width: 100%;
`;

const SDivBtn = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  flex: 0 0 15%;
  width: 100%;
`;

const SDivQcuQcm = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 70%;
  flex-wrap: wrap;
  justify-content: center;

  width: 100%;
`;

const SDivBlur = styled.div<{ $withBackground?: boolean }>`
  display: flex;
  background-color: ${({ $withBackground }) =>
    $withBackground ? '#56afdc80' : 'transparent'};
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
